import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

const GIB = 1024 * 1024 * 1024;

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / GIB).toFixed(2)} GB`;
}

interface Tier {
  name: string;
  bytes: number;
  blurb: string;
}

const TIERS: Tier[] = [
  { name: "Free", bytes: 5 * GIB, blurb: "Get going. Personal use, light shoots." },
  { name: "Pro", bytes: 50 * GIB, blurb: "Working photographer. Weddings, brand, family." },
  { name: "Studio", bytes: 250 * GIB, blurb: "Studio or team. Heavy delivery, longer retention." },
];

export function AccountPage() {
  const { t } = useTranslation();
  const [usage, setUsage] = useState<Awaited<ReturnType<typeof api.getAccountUsage>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retentionDraft, setRetentionDraft] = useState<string>("");
  const [savingRetention, setSavingRetention] = useState(false);
  const [archiveDaysDraft, setArchiveDaysDraft] = useState<string>("90");
  const [nudgeDaysDraft, setNudgeDaysDraft] = useState<string>("7");
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);
  const [interestState, setInterestState] = useState<Record<string, "idle" | "sending" | "sent">>({});

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [u, bs] = await Promise.all([
        api.getAccountUsage(),
        api.getBookingSettings().catch(() => ({ booking_archive_days: 90, nudge_stale_days: 7 })),
      ]);
      setUsage(u);
      setRetentionDraft(u.retention_default_days?.toString() ?? "");
      setArchiveDaysDraft(bs.booking_archive_days.toString());
      setNudgeDaysDraft(bs.nudge_stale_days.toString());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load account");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function saveRetention() {
    setSavingRetention(true);
    try {
      const parsed = retentionDraft.trim() === "" ? null : parseInt(retentionDraft, 10);
      if (parsed !== null && (isNaN(parsed) || parsed < 1 || parsed > 3650)) {
        setError("Retention must be between 1 and 3650 days, or empty to keep forever");
        return;
      }
      await api.setRetentionDefault(parsed);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save retention");
    } finally {
      setSavingRetention(false);
    }
  }

  async function saveBookingSettings() {
    setSavingBookingSettings(true);
    try {
      const archiveDays = parseInt(archiveDaysDraft, 10);
      const nudgeDays = parseInt(nudgeDaysDraft, 10);
      if (isNaN(archiveDays) || archiveDays < 1 || archiveDays > 3650) {
        setError("Archive days must be between 1 and 3650"); return;
      }
      if (isNaN(nudgeDays) || nudgeDays < 1 || nudgeDays > 90) {
        setError("Nudge days must be between 1 and 90"); return;
      }
      await api.updateBookingSettings({ booking_archive_days: archiveDays, nudge_stale_days: nudgeDays });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSavingBookingSettings(false);
    }
  }

  async function sendInterest(tierName: string) {
    setInterestState((prev) => ({ ...prev, [tierName]: "sending" }));
    try {
      await api.recordUpgradeInterest(tierName);
      setInterestState((prev) => ({ ...prev, [tierName]: "sent" }));
    } catch {
      setInterestState((prev) => ({ ...prev, [tierName]: "idle" }));
    }
  }

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading…</p>;
  if (!usage) return <div className="alert alert-error">{error ?? "No data"}</div>;

  const pct = Math.min(100, Math.round((usage.bytes_used / usage.bytes_limit) * 100));
  const overQuota = usage.bytes_used > usage.bytes_limit;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
        <h1 style={{ margin: 0 }}>{t("account.title")}</h1>
        <span style={{ color: "var(--muted)", fontSize: "0.875rem" }}>{t("account.retention_section")}</span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Storage gauge */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <strong>Storage — {usage.plan_name} plan</strong>
          <span style={{ color: overQuota ? "#ef4444" : "var(--muted)", fontSize: "0.875rem" }}>
            {formatBytes(usage.bytes_used)} of {formatBytes(usage.bytes_limit)} used ({pct}%)
          </span>
        </div>
        <div style={{
          height: 12, borderRadius: 6, overflow: "hidden",
          background: "var(--border)", marginBottom: "0.75rem",
        }}>
          <div style={{
            width: `${pct}%`, height: "100%",
            background: overQuota ? "#ef4444" : pct > 85 ? "#f59e0b" : "var(--accent)",
            transition: "width .3s",
          }} />
        </div>
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", color: "var(--muted)", fontSize: "0.875rem" }}>
          <span>{usage.asset_count} photo{usage.asset_count === 1 ? "" : "s"}</span>
          <span>{usage.gallery_count} galler{usage.gallery_count === 1 ? "y" : "ies"}</span>
        </div>
        {overQuota && (
          <p style={{ marginTop: "0.75rem", color: "#ef4444", fontSize: "0.875rem" }}>
            ⚠ You're over your storage limit. Future uploads will be rejected until you remove some galleries or upgrade your plan.
          </p>
        )}
      </div>

      {/* Retention default */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <strong>{t("account.retention_section")}</strong>
          <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>Auto-clean delivered galleries</span>
        </div>
        <p style={{ color: "var(--muted)", fontSize: "0.875rem", margin: "0 0 0.75rem" }}>
          {t("account.retention_desc")}
        </p>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="number" min={1} max={3650}
            placeholder="No default"
            value={retentionDraft}
            onChange={(e) => setRetentionDraft(e.target.value)}
            style={{ width: 140 }}
          />
          <span style={{ color: "var(--muted)" }}>{t("account.retention_days")}</span>
          <button className="btn-primary" onClick={() => void saveRetention()} disabled={savingRetention}>
            {savingRetention ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Bookings board settings */}
      <div className="card">
        <strong>{t("account.booking_settings")}</strong>
        <p style={{ color: "var(--muted)", fontSize: "0.875rem", margin: "0.25rem 0 0.75rem" }}>
          {t("account.booking_settings_desc")}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <label style={{ minWidth: 180 }}>{t("account.archive_label")}</label>
            <input type="number" min={1} max={3650} value={archiveDaysDraft} onChange={e => setArchiveDaysDraft(e.target.value)} style={{ width: 80 }} />
            <span style={{ color: "var(--muted)" }}>{t("account.archive_days")}</span>
            <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{t("account.archive_default")}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <label style={{ minWidth: 180 }}>{t("account.nudge_label")}</label>
            <input type="number" min={1} max={90} value={nudgeDaysDraft} onChange={e => setNudgeDaysDraft(e.target.value)} style={{ width: 80 }} />
            <span style={{ color: "var(--muted)" }}>{t("account.nudge_days")}</span>
            <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{t("account.nudge_note")}</span>
          </div>
          <button className="btn-primary" style={{ alignSelf: "flex-start" }} onClick={() => void saveBookingSettings()} disabled={savingBookingSettings}>
            {savingBookingSettings ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <strong>{t("account.plans_section")}</strong>
          <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>{t("account.plans_note")}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" }}>
          {TIERS.map((tier) => {
            const isCurrent = usage.plan_name === tier.name;
            const state = interestState[tier.name] ?? "idle";
            return (
              <div
                key={tier.name}
                style={{
                  border: `2px solid ${isCurrent ? "var(--accent)" : "var(--border)"}`,
                  borderRadius: "0.5rem",
                  padding: "1rem",
                  background: isCurrent ? "color-mix(in srgb, var(--accent) 7%, transparent)" : "var(--card)",
                  display: "flex", flexDirection: "column", gap: "0.4rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <strong style={{ fontSize: "1.05rem" }}>{tier.name}</strong>
                  {isCurrent && <span style={{ fontSize: "0.7rem", color: "var(--accent)", fontWeight: 600 }}>CURRENT</span>}
                </div>
                <div style={{ color: "var(--muted)", fontSize: "0.875rem" }}>{formatBytes(tier.bytes)} storage</div>
                <p style={{ margin: 0, color: "var(--muted)", fontSize: "0.85rem" }}>{tier.blurb}</p>
                {!isCurrent && (
                  <button
                    className="btn-ghost"
                    style={{ marginTop: "0.5rem", fontSize: "0.875rem" }}
                    disabled={state !== "idle"}
                    onClick={() => void sendInterest(tier.name)}
                  >
                    {state === "sent" ? t("account.upgrade_sent") : state === "sending" ? "Sending…" : t("account.upgrade_interest")}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
